/**
 * Weidner Via6 FutureResidentsClub override.
 *
 * Forked from the rise theme so the Future Residents Club page always
 * renders its body copy and graphics on the Via6 site, even when the
 * runtime's tokenDefinitionIndex hasn't been seeded with the parent
 * component's contentSchema. The parent component calls
 * `useContentToken('futureresidentsclub.title')` (etc.) with no inline
 * options and renders `{title}` directly — so if the schema lookup
 * misses, the headings, descriptions, and CTA copy collapse to empty
 * tags and the page paints blank above the inline SVG icons.
 *
 * The forked component below passes the same default strings to
 * `useContentToken` explicitly (and uses `RichTextContent`, which routes
 * `defaultValue` through to `useContentToken`'s option arg) so the
 * defaults render unconditionally. Editor behavior is preserved because
 * the contentSchema declaration at the bottom of this file is still
 * picked up by the registry-scanning SSG entry — editors can override
 * any of these tokens or slot groups exactly as they could on the
 * parent.
 *
 * The scroll-reveal hooks were dropped from the fork to remove a
 * client-only dependency that could leave the section invisible if
 * IntersectionObserver never crosses its 15% threshold on tall layouts;
 * the simple `.frc-section` styles below paint the content immediately.
 */
import React, { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const howItWorksImageDef = {
  label: 'How It Works Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Default content surfaced both at render time (via the explicit
// `defaultValue` arg to useContentToken) and at editor time (via the
// contentSchema below). Keeping the strings in one place avoids drift
// between what the visitor sees and what the editor shows as the
// "reset to default" value.
const DEFAULTS = {
  title: 'All the <span class="vibe-cursive-accent">perks</span>',
  subtitle:
    "When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:",
  howTitle: 'How it <span class="vibe-cursive-accent">works</span>',
  howBody:
    "<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>",
  benefit1Title: 'Pre-Approved',
  benefit1Desc:
    'Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee).',
  benefit2Title: 'First Choice',
  benefit2Desc:
    'As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market.',
  benefit3Title: 'Priority',
  benefit3Desc: 'Receive first choice and flexibility of the time-frame that you want on move-in day.',
  benefit4Title: 'Commitment Free',
  benefit4Desc:
    "Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities.",
  benefit5Title: 'Get to Know Us',
  benefit5Desc:
    'You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in.',
  benefit6Title: 'Joining Is Easy',
  benefit6Desc:
    'Simply fill out an online application by selecting your desired floor plan, select a "wait list" unit, pay a holding deposit, and we\'ll hold your spot in line. Easy!',
};

// ── Inline SVG icons (one per benefit card) ──
// Sourced from the via6living.com Future Residents Club page so the
// Via6 site matches the original Duda-built design 1:1. Each icon is
// a 100×100 filled glyph that paints with `currentColor` so it
// inherits the surrounding text color (text-on-primary on this
// section). Kept as plain JSX components so they render at SSG time
// with no client deps.

function IconPreApproved() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m86.734 48.844c-1.6406-1.625-4.2969-1.625-5.9219 0.015625 0 0-16.633 16.691-17.734 17.797l-6.0156-6.1719c-1.6094-1.6562-4.2656-1.6875-5.9219-0.078125s-1.6875 4.2656-0.078125 5.9219l8.7656 9c1.6758 1.7773 4.7266 1.8164 6.4062 0.03125l20.516-20.594c1.625-1.6406 1.625-4.2969-0.015625-5.9219z" />
      <path d="m25.234 86.984c-3.1094 0-5.6406-2.5469-5.6406-5.6562v-65.969h-5.0312c-1.8281 0-3.3125 1.4844-3.3125 3.3125v73.328c0 1.8281 1.4844 3.3125 3.3125 3.3125h44.25c1.8125 0 3.2969-1.4844 3.2969-3.3125v-5.0156z" />
      <path d="m56.609 20.266c1.6133 0.003906 12.793 0 14.109 0l-14.297-13.594c0.007813 1.1758-0.003906 11.938 0 13.406 0 0.09375 0.078125 0.1875 0.1875 0.1875z" />
      <path d="m68.453 77.562c-2.8438 3.0039-8.0391 2.9648-10.859-0.046875 0 0-8.7656-9-8.7656-9-6.5352-7.1484 3.5078-16.918 10.469-10.219 0 0 3.8125 3.9062 3.8125 3.9062 1.1328-1.1289 7.9375-7.9805 8.9062-8.9375v-29.875c-0.33594 0.089844-14.613-0.066406-15.406 0-1.8281 0-3.3125-1.4844-3.3125-3.3125v-14.609h-28.062c-1.3906 0-2.5156 1.1406-2.5156 2.5312v73.328c0 1.3906 1.125 2.5312 2.5156 2.5312h44.25c1.3906 0 2.5312-1.1406 2.5312-2.5312v-7.3438c-0.85547 0.88672-2.6914 2.6953-3.5625 3.5781zm-6.5938-29.672h-29c-2.043-0.035156-2.0586-3.0859 0-3.125h29c2.043 0.035156 2.0586 3.0859 0 3.125zm-30.562-9.3594c0-0.875 0.70312-1.5625 1.5625-1.5625h14.5c0.85938 0 1.5625 0.6875 1.5625 1.5625 0 0.85938-0.70312 1.5625-1.5625 1.5625h-14.5c-0.85938 0-1.5625-0.70312-1.5625-1.5625zm30.562 1.5625h-9.625c-0.875 0-1.5625-0.70312-1.5625-1.5625 0-0.875 0.6875-1.5625 1.5625-1.5625h9.625c2.0508 0.027344 2.0586 3.0859 0 3.125zm0-10.938c2.043 0.035156 2.0586 3.0859 0 3.125h-29c-2.043-0.035156-2.0586-3.0859 0-3.125h29z" />
    </svg>
  );
}

function IconFirstChoice() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m25 26.656h27.672v3.125h-27.672z" />
      <path d="m47.328 47.262h27.672v3.125h-27.672z" />
      <path d="m25 33.523h50v3.125h-50z" />
      <path d="m25 40.391h50v3.125h-50z" />
      <path d="m84.375 68.234v-41.086l-23.242-23.242h-39.258c-3.4531 0-6.25 2.7969-6.25 6.25v52.723c-0.96484 0.55078-1.5586 1.5781-1.5625 2.6914v22.711c0.003906 4.3125 3.5 7.8086 7.8125 7.8125h56.25c4.3125-0.003906 7.8086-3.5 7.8125-7.8125v-17.355c-0.003906-1.1133-0.59766-2.1367-1.5625-2.6914zm-41.172 21.621h-7.0312v-2.3242h2.1016v-8.9258h-2.4219v-1.9883c0.55078-0.015626 1.0977-0.082032 1.6328-0.19531 0.53906-0.10938 1.0391-0.36719 1.4453-0.74219h2.1562v11.852h2.1172zm10.648-0.89844v-0.003906c-1.0586 0.78516-2.3594 1.1758-3.6719 1.0938-1.043-0.003906-2.0664-0.28125-2.9688-0.80469-0.50781-0.26953-0.95703-0.62891-1.332-1.0586-0.41797-0.5-0.75-1.0586-0.99609-1.6602l2.5-1.0938c0.14063 0.30859 0.30469 0.60547 0.48828 0.89062 0.16406 0.25781 0.36719 0.48828 0.60156 0.68359 0.23047 0.19141 0.49609 0.33984 0.78125 0.44141 0.32031 0.10938 0.66016 0.16406 1 0.15625 0.25781 0 0.51562-0.023437 0.76953-0.074218 0.23437-0.046875 0.46094-0.12891 0.66406-0.25391 0.19922-0.11719 0.35938-0.28516 0.46875-0.48828 0.12109-0.24219 0.17969-0.51172 0.16797-0.78125-0.011719-0.48438-0.28516-0.92969-0.71094-1.1602-0.59375-0.38672-1.2305-0.69922-1.8945-0.9375-1.1641-0.39844-2.2578-0.97266-3.2422-1.707-0.76172-0.66406-1.1797-1.6367-1.125-2.6445-0.019531-1.0742 0.46484-2.0938 1.3125-2.7578 0.92578-0.74219 2.0898-1.1328 3.2812-1.0938 1.043-0.015625 2.0742 0.26562 2.9688 0.80859 0.44141 0.26562 0.83594 0.60938 1.1602 1.0117 0.37891 0.48047 0.6875 1.0117 0.92188 1.5742l-2.7578 0.84375c-0.19141-0.48828-0.48438-0.92969-0.85547-1.3008-0.39844-0.35156-0.92187-0.53125-1.4531-0.5-0.20313 0.003906-0.40625 0.023437-0.60938 0.066406-0.19531 0.042969-0.38672 0.10938-0.5625 0.20703-0.36328 0.19922-0.58594 0.58594-0.57031 1.0039 0.003906 0.19531 0.066406 0.38281 0.17969 0.54297 0.16016 0.21094 0.37109 0.38672 0.60937 0.50781 0.64844 0.35547 1.3281 0.66016 2.0312 0.89844 1.0273 0.34375 1.9883 0.85547 2.8477 1.5195 0.89844 0.67969 1.3984 1.7617 1.332 2.8867 0.066406 1.207-0.42578 2.3828-1.3359 3.1797zm12.32-10.5-3.543-0.003906v11.406h-2.9805v-11.406h-3.5273v-2.5469h10.051zm15.078-10.656h-34.242l-3.75-4.2891c-0.58984-0.67969-1.4492-1.0664-2.3516-1.0664h-22.156v-52.289c0-1.7266 1.3984-3.125 3.125-3.125h37.047v16.078c0 1.6562 0.66016 3.2461 1.832 4.418s2.7617 1.832 4.418 1.832h16.078z" />
    </svg>
  );
}

function IconPriority() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m23.426 29.324c-0.53906 0-0.97656 0.4375-0.97656 0.97656v54.133h-4.8633v-54.133c0-0.53906-0.4375-0.97656-0.97656-0.97656h-8.0469l11.457-13.242 11.457 13.242z" />
      <path d="m92.598 26.133h-51.125v-8.3438h51.125z" />
      <path d="m83.754 45.566h-42.277v-8.3438h42.277z" />
      <path d="m71.57 65h-30.098v-8.3438h30.098z" />
      <path d="m58.809 84.434h-17.336v-8.3438h17.336z" />
    </svg>
  );
}

function IconCommitmentFree() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m66.57 12.602c-6.25 0-12.141 2.4297-16.559 6.8594-4.4219-4.4219-10.32-6.8594-16.57-6.8594s-12.148 2.4297-16.578 6.8711c-4.4297 4.4219-6.8711 10.32-6.8711 16.578 0 6.2617 2.4297 12.141 6.8711 16.559l0.62109 0.62109c-1.9219 2.9102-1.6094 6.8711 0.94922 9.4219 1.4492 1.4609 3.3711 2.1992 5.2891 2.1992 0.011719 0 0.03125-0.011718 0.039063-0.011718-0.011719 1.9414 0.71094 3.8711 2.1797 5.3398 1.4609 1.4609 3.3711 2.1797 5.3008 2.1797h0.039062s-0.011719 0.03125-0.011719 0.039063c0 2 0.78125 3.8789 2.1914 5.3008 1.4609 1.4609 3.3711 2.1797 5.2891 2.1797h0.050781c-0.011719 1.9219 0.71094 3.8594 2.1797 5.3281 1.4102 1.4219 3.2891 2.1914 5.2891 2.1914s3.8789-0.78125 5.2891-2.1914l6.3008-6.2891c0.050781-0.050781 0.10156-0.12109 0.14844-0.17188l0.32031 0.30859c1.4219 1.4219 3.2891 2.1992 5.2891 2.1992s3.8789-0.78125 5.2891-2.1992c1.1992-1.1992 1.8984-2.6992 2.1094-4.2617 0.44141 0.078125 0.87891 0.12109 1.3203 0.12109 1.9102 0 3.8281-0.73828 5.2891-2.1992 2.1719-2.1719 2.7188-5.3711 1.6484-8.0703 1.2383-0.32031 2.4102-0.96094 3.3789-1.9219l0.089844-0.10156c1.3594-1.4102 2.1094-3.2383 2.1094-5.1992 0-1.7695-0.62109-3.4609-1.75-4.8008 0.011719-0.011719 0.019532-0.011719 0.019532-0.019532 9.1406-9.1406 9.1406-24 0-33.141-4.4219-4.4297-10.32-6.8711-16.578-6.8711zm-45.09 46.977c-1.2188-1.2188-1.2188-3.2305 0-4.4688l6.4688-6.4609c0.58984-0.58984 1.3789-0.92188 2.2305-0.92188h0.14844c0.89844 0.039063 1.7188 0.44922 2.2891 1.1602 1.0195 1.2305 0.85156 3.1602-0.37109 4.3906l-6.3008 6.2891c-1.2188 1.2305-3.2305 1.2305-4.4688 0zm7.5312 7.5312c-1.2305-1.2305-1.2305-3.2305 0-4.4688l6.4492-6.4492c0.60937-0.58984 1.3789-0.92188 2.2305-0.92188h0.16016c0.89844 0.039063 1.7109 0.44922 2.2891 1.1602 1.0195 1.2305 0.85156 3.1602-0.37891 4.3906l-6.2891 6.2891c-1.2305 1.2305-3.2305 1.2305-4.4688 0zm7.5312 7.5312c-0.58984-0.58984-0.92969-1.3906-0.92969-2.2383 0-0.85156 0.33984-1.6289 0.92969-2.2305l6.2891-6.2891s0-0.011718 0.011719-0.011718l0.14844-0.14844c0.60937-0.60938 1.3789-0.92969 2.2305-0.92969 0.050782 0 0.10938 0 0.14844 0.011719 0.91016 0.039062 1.7188 0.44922 2.3008 1.1602 0.44141 0.53906 0.67188 1.2188 0.67188 1.9102 0 0.87891-0.35938 1.7812-1.0508 2.4805l-6.2891 6.2891c-1.2305 1.2305-3.2305 1.2305-4.4688 0zm18.281 1.2188-6.3008 6.3008c-1.1914 1.1914-3.2695 1.1914-4.4492 0-1.1797-1.1914-1.2305-3.2383 0-4.4688l6.2891-6.3008 0.16016-0.16016c0.58984-0.58984 1.3789-0.92188 2.2305-0.92188h0.14844c0.89844 0.039062 1.7188 0.44922 2.2891 1.1602 0.44922 0.53906 0.67188 1.2188 0.67188 1.9102 0 0.89062-0.35938 1.7891-1.0391 2.4805zm24.801-16.199-0.03125 0.03125c-1.2188 1.1914-3.1797 1.1914-4.3984 0l-15.57-15.57c-0.42188-0.42188-0.96875-0.62891-1.5195-0.62891s-1.1094 0.21094-1.5391 0.62891c-0.83984 0.83984-0.83984 2.2188 0 3.0586l15.539 15.539s0.039062 0.03125 0.058593 0.050781l2.4219 2.4297c1.2305 1.2188 1.2305 3.2305 0 4.4688-1.2305 1.2188-3.2305 1.2188-4.4688 0l-18.02-18.02c-0.42188-0.42188-0.96875-0.62891-1.5195-0.62891s-1.1016 0.21094-1.5195 0.62891c-0.83984 0.83984-0.83984 2.2109 0 3.0508l16.82 16.828c1.2305 1.2305 1.2305 3.2305 0 4.4688-1.2305 1.2383-3.2695 1.1914-4.4492 0l-1.3516-1.3516c0.35156-2.0781-0.14844-4.2617-1.5391-5.9297-1.3516-1.6289-3.3203-2.6289-5.4297-2.7188-0.14844-0.011719-0.30078 0-0.46094 0 0.03125-1.7188-0.5-3.4297-1.6289-4.8008-1.3398-1.6289-3.3203-2.6289-5.4219-2.7188h-0.46094c0.019531-1.7188-0.51172-3.4297-1.6406-4.8008-1.3398-1.6289-3.3203-2.6211-5.4219-2.7188-0.16016 0-0.30859 0-0.46094 0.011719 0.03125-1.7188-0.5-3.4414-1.6289-4.8086-1.3516-1.6211-3.3281-2.6211-5.4297-2.7109-2.1016-0.10938-4.1602 0.69141-5.6406 2.1797l-4.4492 4.4492-0.51172-0.51172c-3.6094-3.6094-5.6016-8.4102-5.6016-13.52s1.9883-9.8984 5.6016-13.52c3.6094-3.6094 8.4102-5.6016 13.52-5.6016s9.8906 1.9883 13.512 5.5898l-5.0195 5.0195c-1.4102 1.4219-2.1797 3.2891-2.1797 5.2891s0.78125 3.8789 2.1797 5.3008c2.9219 2.9219 7.6719 2.9219 10.59 0l5.0195-5.0195 22.09 22.09c0.60938 0.60938 0.92969 1.3906 0.92969 2.2383 0 0.85156-0.33984 1.6289-0.92969 2.2305zm0.51953-10.148-0.039062 0.039062-19.512-19.512 0.66016-0.66016 0.039063-0.050781c0.85156-0.83984 0.85156-2.2109 0-3.0508-0.83984-0.85156-2.2109-0.85156-3.0508 0l-8.7891 8.7812c-1.2188 1.2305-3.2305 1.2305-4.4688 0-0.58984-0.58984-0.92188-1.3906-0.92188-2.2383 0-0.85156 0.32031-1.6289 0.92188-2.2305l8.0703-8.0703c3.6094-3.6094 8.4102-5.6016 13.52-5.6016s9.8984 1.9883 13.52 5.6016c7.4414 7.4297 7.4492 19.52 0.050781 26.98z" />
      <path d="m29.23 21.859c-4.9297 0.60938-9.3594 5.0391-9.9688 9.9688-0.14844 1.1914 1.4883 1.6602 2.0391 0.60156 0.21094-0.39844 0.42188-0.78125 0.64844-1.1602 1.9414-3.2812 4.6484-5.6602 7.9102-7.3906 1.0391-0.55078 0.53125-2.1719-0.64062-2.0195z" />
    </svg>
  );
}

function IconGetToKnow() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m91.934 90.508c-2.0938 0.011719-6.5938-0.007812-8.6211 0v-77.633l-31.695 0.003906v77.629h-3.2383v-69.887h-33.316c-0.42969 0-0.84375 0.17188-1.1484 0.47266-0.30078 0.30469-0.47266 0.71875-0.47266 1.1484v68.266h-5.3789c-0.88281 0.015626-1.5938 0.73828-1.5938 1.6211 0 0.88672 0.71094 1.6055 1.5938 1.6211h83.871c0.88672-0.011719 1.5977-0.73438 1.5977-1.6211 0-0.88672-0.71094-1.6055-1.5977-1.6211zm-16.105-72.445c0.011719-0.88672 0.73438-1.5977 1.6172-1.5977 0.88672 0 1.6094 0.71094 1.6211 1.5977v7.0312c0 0.89453-0.72266 1.6211-1.6211 1.6211-0.89453 0-1.6172-0.72656-1.6172-1.6211zm0 13.871c0.015625-0.88281 0.73438-1.5898 1.6172-1.5898 0.88672 0 1.6055 0.70703 1.6211 1.5898v7.0312c-0.011718 0.88672-0.73438 1.5977-1.6211 1.5977-0.88281 0-1.6055-0.71094-1.6172-1.5977zm-0.24219 14.859h-0.003907c0.015625-0.88672 0.73828-1.5977 1.6211-1.5977 0.88672 0 1.6094 0.71094 1.6211 1.5977v7.0312c0 0.89453-0.72656 1.6211-1.6211 1.6211s-1.6211-0.72656-1.6211-1.6211zm0 13.84-0.003907-0.003906c0.015625-0.88281 0.73828-1.5938 1.6211-1.5938 0.88672 0 1.6094 0.71094 1.6211 1.5938v7.0508c-0.011719 0.88281-0.73438 1.5938-1.6211 1.5938-0.88281 0-1.6055-0.71094-1.6211-1.5938zm-9.3672-42.566v-0.003906c0.011719-0.88672 0.73438-1.5977 1.6211-1.5977 0.88281 0 1.6055 0.71094 1.6172 1.5977v7.0312c0 0.89453-0.72266 1.6211-1.6172 1.6211-0.89453 0-1.6211-0.72656-1.6211-1.6211zm0 13.871v-0.003906c0.015625-0.88281 0.73438-1.5898 1.6211-1.5898 0.88281 0 1.6016 0.70703 1.6172 1.5898v7.0312c-0.011719 0.88672-0.73438 1.5977-1.6172 1.5977-0.88672 0-1.6094-0.71094-1.6211-1.5977zm-0.24219 14.859v-0.003906c0.011719-0.88672 0.73438-1.5977 1.6172-1.5977 0.88672 0 1.6094 0.71094 1.6211 1.5977v7.0312c0 0.89453-0.72266 1.6211-1.6211 1.6211-0.89453 0-1.6172-0.72656-1.6172-1.6211zm0 13.84v-0.007813c0.011719-0.88281 0.73438-1.5938 1.6172-1.5938 0.88672 0 1.6055 0.71094 1.6211 1.5938v7.0508c-0.011719 0.88281-0.73438 1.5938-1.6211 1.5938-0.88281 0-1.6055-0.71094-1.6172-1.5938zm-8.8633-42.574c0.011719-0.88672 0.73438-1.5977 1.6172-1.5977 0.88672 0 1.6094 0.71094 1.6211 1.5977v7.0312c0 0.89453-0.72656 1.6211-1.6211 1.6211s-1.6172-0.72656-1.6172-1.6211zm0 13.871c0.015625-0.88281 0.73438-1.5898 1.6172-1.5898 0.88672 0 1.6055 0.70703 1.6211 1.5898v7.0312c-0.011718 0.88672-0.73437 1.5977-1.6211 1.5977-0.88281 0-1.6055-0.71094-1.6172-1.5977zm-0.24219 14.859h-0.003906c0.015624-0.88672 0.73437-1.5977 1.6211-1.5977s1.6094 0.71094 1.6211 1.5977v7.0312c-0.011719 0.88672-0.73438 1.5977-1.6211 1.5977s-1.6055-0.71094-1.6211-1.5977zm0 13.84-0.003906-0.003906c0.015624-0.88672 0.73437-1.5977 1.6211-1.5977s1.6094 0.71094 1.6211 1.5977v7.0508c-0.011719 0.88281-0.73438 1.5977-1.6211 1.5977s-1.6055-0.71484-1.6211-1.5977zm-18.746 29.879-11.605-0.003907v-13.902h-4.4727c-0.89453 0-1.6211-0.72266-1.6211-1.6211 0-0.89453 0.72656-1.6172 1.6211-1.6172h20.969c0.88281 0.011718 1.5938 0.73437 1.5938 1.6172 0 0.88672-0.71094 1.6094-1.5938 1.6211-1.0234-0.003907-3.832 0.003906-4.8945 0zm-15.543-48.938c10.395 0.19141 8.4336-1.7969 8.6211 8.6055 0 0.42969-0.17188 0.83984-0.47266 1.1445-0.30469 0.30469-0.71875 0.47656-1.1484 0.47266h-7c-0.42969 0.003906-0.84375-0.16797-1.1484-0.47266-0.30469-0.30469-0.47266-0.71484-0.47266-1.1445v-6.9844c0.003906-0.89453 0.72656-1.6172 1.6211-1.6211zm-1.6211-4.0195c0.1875-10.414-1.7852-8.4297 8.6211-8.6211 0.89453 0.003906 1.6172 0.72656 1.6211 1.6211v7c-0.003906 0.89453-0.72656 1.6172-1.6211 1.6211h-7c-0.89453-0.003906-1.6172-0.72656-1.6211-1.6211zm1.6211 17.207c10.414 0.1875 8.4297-1.7852 8.6211 8.6211-0.003906 0.89453-0.72656 1.6211-1.6211 1.6211h-7c-0.89453 0-1.6172-0.72656-1.6211-1.6211v-7c0.003906-0.89062 0.72656-1.6172 1.6211-1.6172zm12.898-13.191c10.391 0.1875 8.4141-1.7773 8.6055 8.6055l-0.003907 0.003907c0.003907 0.42969-0.16797 0.83984-0.47266 1.1445-0.30469 0.30469-0.71484 0.47656-1.1445 0.47266h-6.9844c-0.42969 0.003906-0.84375-0.16797-1.1484-0.47266-0.30469-0.30469-0.47266-0.71484-0.47266-1.1445v-6.9844c0.003906-0.89453 0.72656-1.6172 1.6211-1.6211zm-1.6211-4.0195c0.19141-10.395-1.7969-8.4336 8.6055-8.6211v0.003906c0.89062 0.003906 1.6172 0.72656 1.6172 1.6211v7c0 0.89453-0.72656 1.6172-1.6172 1.6211h-6.9844c-0.89453-0.003906-1.6172-0.72656-1.6211-1.6211zm1.6211 17.207c10.41 0.1875 8.4062-1.7578 8.6016 8.625 0 0.89453-0.72656 1.6211-1.6172 1.6211h-6.9844c-0.89453 0-1.6172-0.72656-1.6211-1.6211v-7c0.003906-0.89062 0.72656-1.6172 1.6211-1.6172z" />
      <path d="m24.203 58.004h3.7578v3.7578h-3.7578z" />
      <path d="m24.203 44.816h3.7578v3.7422h-3.7578z" />
      <path d="m37.102 44.816h3.7422v3.7422h-3.7422z" />
      <path d="m37.102 32.176h3.7422v3.7578h-3.7422z" />
      <path d="m24.203 32.176h3.7578v3.7578h-3.7578z" />
      <path d="m37.102 58.004h3.7422v3.7578h-3.7422z" />
      <path d="m48.879 6.25h37.188v3.3711h-37.188z" />
    </svg>
  );
}

function IconJoiningEasy() {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" width="56" height="56" aria-hidden="true">
      <path d="m61.625 31.719c0 9.0625-7.3438 16.406-16.406 16.406s-16.406-7.3438-16.406-16.406 7.3438-16.406 16.406-16.406 16.406 7.3438 16.406 16.406" />
      <path d="m68.5 51.875c-9.0312 0-16.375 7.3438-16.375 16.406 0 9.0312 7.3438 16.406 16.375 16.406 9.0625 0 16.406-7.375 16.406-16.406 0-9.0625-7.3438-16.406-16.406-16.406zm5.125 18.742h-2.7695v2.7656c0 1.293-1.0508 2.3438-2.3438 2.3438s-2.3438-1.0508-2.3438-2.3438v-2.7656h-2.7656c-1.293 0-2.3438-1.0508-2.3438-2.3438 0-1.293 1.0508-2.3438 2.3438-2.3438h2.7656v-2.7656c0-1.293 1.0508-2.3438 2.3438-2.3438s2.3438 1.0508 2.3438 2.3438v2.7656h2.7695c1.293 0 2.3438 1.0508 2.3438 2.3438 0 1.293-1.0469 2.3438-2.3438 2.3438z" />
      <path d="m54.969 53.5c-4.2188 3.875-6.5625 9.125-6.5625 14.781 0 4.4375 1.4688 8.6875 4.25 12.25 0.375 0.5 0.4375 1.125 0.1875 1.6562-0.28125 0.53125-0.8125 0.875-1.4062 0.875h-34.781c-0.84375 0-1.5625-0.6875-1.5625-1.5625v-8.1875c0-12.438 10.125-22.531 22.531-22.531h16.312c0.625 0 1.2188 0.375 1.4375 1 0.25 0.59375 0.09375 1.2812-0.40625 1.7188z" />
    </svg>
  );
}

export function FutureResidentsClub() {
  // All editable text on this section is bound through RichTextContent
  // (type: 'richtext' in the schema below). RichTextContent renders the
  // resolved HTML itself, so there are no useContentToken children to
  // wire here — only the slot groups still need hooks.
  const howImage = useSlotGroup('futureresidentsclub.how_image', howItWorksImageDef);
  const ctaLink = useSlotGroup('futureresidentsclub.cta_link', ctaLinkDef);

  const imageSrc = howImage.image;
  const imageAlt = howImage.alt || 'Apartment interior';

  // Lazy-load the "Satisfy" cursive Google Font used by the
  // .vibe-cursive-accent span in the title. De-duped against other
  // Weidner components (PropertyOverview, AmenitiesGrid, ...) that
  // already load this same face via the `data-vibe-font="satisfy"`
  // attribute on the <link>.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="frc-section">
      <div className="frc-container">
        {/* All the perks */}
        <div className="frc-heading">
          <RichTextContent
            path="futureresidentsclub.title"
            label="Title"
            defaultValue={DEFAULTS.title}
            as="h2"
            className="frc-title"
          />
          <RichTextContent
            path="futureresidentsclub.subtitle"
            label="Description"
            defaultValue={DEFAULTS.subtitle}
            as="p"
            className="frc-subtitle"
          />
        </div>
      </div>

      {/* Full-width divider above the benefits grid. Placed as a direct
         child of .frc-section (which has no max-width) so it spans the
         full browser width, unlike the inner .frc-container. */}
      <div className="frc-divider" aria-hidden="true" />

      <div className="frc-container">
        {/* Benefits grid */}
        <div className="frc-benefits-grid">
          <div className="frc-card">
            <div className="frc-card-icon"><IconPreApproved /></div>
            <RichTextContent path="futureresidentsclub.benefit1_title" label="Pre-Approved Title" defaultValue={DEFAULTS.benefit1Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit1_desc" label="Pre-Approved Description" defaultValue={DEFAULTS.benefit1Desc} as="p" className="frc-card-desc" />
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconFirstChoice /></div>
            <RichTextContent path="futureresidentsclub.benefit2_title" label="First Choice Title" defaultValue={DEFAULTS.benefit2Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit2_desc" label="First Choice Description" defaultValue={DEFAULTS.benefit2Desc} as="p" className="frc-card-desc" />
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconPriority /></div>
            <RichTextContent path="futureresidentsclub.benefit3_title" label="Priority Title" defaultValue={DEFAULTS.benefit3Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit3_desc" label="Priority Description" defaultValue={DEFAULTS.benefit3Desc} as="p" className="frc-card-desc" />
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconCommitmentFree /></div>
            <RichTextContent path="futureresidentsclub.benefit4_title" label="Commitment Free Title" defaultValue={DEFAULTS.benefit4Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit4_desc" label="Commitment Free Description" defaultValue={DEFAULTS.benefit4Desc} as="p" className="frc-card-desc" />
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconGetToKnow /></div>
            <RichTextContent path="futureresidentsclub.benefit5_title" label="Get to Know Us Title" defaultValue={DEFAULTS.benefit5Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit5_desc" label="Get to Know Us Description" defaultValue={DEFAULTS.benefit5Desc} as="p" className="frc-card-desc" />
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconJoiningEasy /></div>
            <RichTextContent path="futureresidentsclub.benefit6_title" label="Joining Is Easy Title" defaultValue={DEFAULTS.benefit6Title} as="h3" className="frc-card-title" />
            <RichTextContent path="futureresidentsclub.benefit6_desc" label="Joining Is Easy Description" defaultValue={DEFAULTS.benefit6Desc} as="p" className="frc-card-desc" />
          </div>
        </div>
      </div>

      {/* Full-width divider above the How It Works image. Same reason
         as the divider above the grid — sits outside .frc-container so
         it stretches edge-to-edge. */}
      <div className="frc-divider" aria-hidden="true" />

      <div className="frc-container">
        {/* How It Works */}
        <div className="frc-how">
          <div className="frc-how-text">
            <RichTextContent
              path="futureresidentsclub.how_title"
              label="How It Works Title"
              defaultValue={DEFAULTS.howTitle}
              as="h3"
              className="frc-how-title"
            />
            <RichTextContent
              path="futureresidentsclub.how_body"
              label="How It Works"
              defaultValue={DEFAULTS.howBody}
              as="div"
              className="frc-how-body"
            />
            <EditableSlotGroup groupPath="futureresidentsclub.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/contact-us'}
                target={ctaLink._target || '_self'}
                className="frc-cta"
              >
                {ctaLink.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>

          <EditableSlotGroup groupPath="futureresidentsclub.how_image" definition={howItWorksImageDef}>
            <div className="frc-how-photo">
              {imageSrc ? (
                <VibesitesImage url={imageSrc} alt={imageAlt} className="frc-how-photo-img" loading="lazy" />
              ) : (
                <div className="frc-how-photo-placeholder" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .frc-section {
          /* Tighter top padding so the "All the perks" title sits
             closer to the divider/image above this section. Bottom
             padding is preserved at the original value. */
          padding: 2rem 0 4rem;
          background: var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .frc-section { padding: 2.5rem 0 5rem; }
        }

        .frc-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .frc-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .frc-container { padding: 0 2rem; } }

        .frc-heading {
          text-align: center;
          margin-bottom: 3.5rem;
        }

        /* Edge-to-edge horizontal rule used to separate the heading,
           benefits grid, and "How It Works" image. Rendered as a
           direct child of .frc-section (which has no max-width) so it
           spans the full browser viewport rather than the 72rem
           container. The color matches the legacy 1px border-top that
           previously lived on .frc-how, but bumped to 2px so the bar
           reads as a deliberate divider rather than a hairline rule. */
        .frc-divider {
          width: 100%;
          height: 2px;
          background: var(--vibe-color-border);
        }
        /* Serif heading family (Via6 Prata). !important because the
           rise theme's base stylesheet and the inline editor runtime's
           injected styles for [data-vibesite-token] elements can
           otherwise win the cascade on h2/h3 nodes that EditableToken
           renders. */
        .frc-title,
        .frc-title [data-vibesite-richtext] {
          font-family: var(--vibe-font-heading) !important;
          font-size: 2.25rem;
          font-weight: 400;
          color: var(--vibe-color-text-on-primary);
          margin: 0 0 1.25rem;
          letter-spacing: 0.025em;
        }
        @media (min-width: 640px) { .frc-title { font-size: 2.75rem; } }
        /* Decorative cursive accent for any <span class="vibe-cursive-accent">
           inside the title (or any editor-authored richtext on this section).
           Matches the Satisfy-based treatment used by PropertyOverview,
           AmenitiesGrid, HomeMap, and the Interior intro/body sections. */
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
        /* Description copy under the title. !important on the
           font-family because the rise theme's base stylesheet and the
           inline editor runtime's injected styles for
           [data-vibesite-token] elements can otherwise win the cascade
           on the <p> EditableToken renders here — without it the
           description inherits the Prata heading face from the section
           heading context on some pages. */
        .frc-subtitle,
        .frc-subtitle[data-vibesite-token],
        .frc-subtitle [data-vibesite-richtext] {
          font-family: var(--vibe-font-body) !important;
          font-size: 1rem;
          color: var(--vibe-color-text-on-primary);
          max-width: 48rem;
          margin: 0 auto;
          line-height: 1.625;
        }

        .frc-benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2.5rem 2rem;
          /* Breathing room above (below the icon-section divider) and
             below (above the how-section divider). */
          margin-top: 3.5rem;
          margin-bottom: 4rem;
        }
        @media (max-width: 768px) {
          .frc-benefits-grid { grid-template-columns: 1fr; gap: 2rem; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .frc-benefits-grid { grid-template-columns: repeat(2, 1fr); }
        }

        .frc-card {
          text-align: center;
          padding: 1rem 0.5rem;
        }
        .frc-card-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem;
          /* Use the theme's secondary brand color so the icons match
             the accent treatment from via6living.com (where titles +
             glyphs are rendered in --color_2). Falls back to a sane
             value if the property hasn't supplied a secondary color
             because the tokens runtime always emits the variable. */
          color: var(--vibe-color-secondary);
        }
        .frc-card-title,
        .frc-card-title [data-vibesite-richtext] {
          font-family: var(--vibe-font-body) !important;
          font-size: 1.1rem;
          font-weight: 500;
          color: var(--vibe-color-secondary);
          margin: 0 0 0.6rem;
        }
        .frc-card-desc,
        .frc-card-desc [data-vibesite-richtext] {
          font-family: var(--vibe-font-body) !important;
          font-size: 0.85rem;
          color: var(--vibe-color-text-on-primary);
          line-height: 1.65;
          margin: 0;
        }
        .frc-card-desc p { margin: 0; }
        .frc-card-desc strong { font-weight: 600; }
        .frc-card-desc a { color: var(--vibe-color-secondary); text-decoration: underline; }

        .frc-how {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: start;
          /* Extra breathing room between the divider above and the
             image / how-it-works copy. Was 2rem with an inline
             border-top hairline; the divider now sits outside this
             container (as a full-width .frc-divider element) so the
             border-top has been removed and the padding bumped to
             give the image more space below the bar. */
          padding-top: 4rem;
        }
        @media (max-width: 768px) {
          .frc-how { grid-template-columns: 1fr; }
        }

        .frc-how-text { padding-top: 0.5rem; }
        .frc-how-title,
        .frc-how-title [data-vibesite-richtext] {
          font-family: var(--vibe-font-heading) !important;
          /* Match the "All the perks" title (.frc-title) sizing so the
             two section headings read as a pair. */
          font-size: 2.25rem;
          font-weight: 400;
          color: var(--vibe-color-text-on-primary);
          margin: 0 0 1.25rem;
          text-align: center;
        }
        @media (min-width: 640px) { .frc-how-title { font-size: 2.75rem; } }
        .frc-how-body {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-on-primary);
          line-height: 1.625;
        }
        .frc-how-body p { margin: 0 0 1rem; }
        .frc-how-body ul { list-style: disc; padding-left: 1.25rem; margin: 0 0 1rem; }
        .frc-how-body li { margin-bottom: 0.5rem; }
        .frc-how-body strong { font-weight: 600; }
        .frc-how-body a { color: var(--vibe-color-primary); text-decoration: underline; }

        .frc-how-photo {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .frc-how-photo-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .frc-how-photo:hover .frc-how-photo-img { transform: scale(1.03); }
        .frc-how-photo-placeholder {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-surface);
        }

        /* Matches the site's standard primary CTA pill (see
           PropertyOverview's "Apply Now" and HomeMap's primary CTA):
           secondary-color fill, flips to a white background with
           secondary-color label on hover. The pill radius is applied
           globally to .frc-cta by the Header's CTA-rounding rule, so
           we don't repeat border-radius here. */
        .frc-cta {
          display: block;
          /* Extra top margin gives the CTA breathing room below the
             how-it-works body copy. */
          margin: 2.25rem auto 0;
          width: 18rem;
          max-width: 100%;
          padding: 0.625rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-align: center;
          text-decoration: none;
          text-transform: uppercase;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          border: 1px solid var(--vibe-color-secondary);
          transition: background-color 150ms ease, color 150ms ease,
            border-color 150ms ease;
        }
        .frc-cta:hover,
        .frc-cta:focus-visible {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FutureResidentsClub',
  displayName: 'Future Residents Club',
  tokens: {
    'futureresidentsclub.title':           { label: 'Title',                       type: 'richtext', defaultValue: DEFAULTS.title },
    'futureresidentsclub.subtitle':        { label: 'Description',                 type: 'richtext', defaultValue: DEFAULTS.subtitle },
    'futureresidentsclub.how_title':       { label: 'How It Works Title',          type: 'richtext', defaultValue: DEFAULTS.howTitle },
    'futureresidentsclub.how_body':        { label: 'How It Works Body',           type: 'richtext', defaultValue: DEFAULTS.howBody },
    'futureresidentsclub.benefit1_title':  { label: 'Pre-Approved Title',          type: 'richtext', defaultValue: DEFAULTS.benefit1Title },
    'futureresidentsclub.benefit1_desc':   { label: 'Pre-Approved Description',    type: 'richtext', defaultValue: DEFAULTS.benefit1Desc },
    'futureresidentsclub.benefit2_title':  { label: 'First Choice Title',          type: 'richtext', defaultValue: DEFAULTS.benefit2Title },
    'futureresidentsclub.benefit2_desc':   { label: 'First Choice Description',    type: 'richtext', defaultValue: DEFAULTS.benefit2Desc },
    'futureresidentsclub.benefit3_title':  { label: 'Priority Title',              type: 'richtext', defaultValue: DEFAULTS.benefit3Title },
    'futureresidentsclub.benefit3_desc':   { label: 'Priority Description',        type: 'richtext', defaultValue: DEFAULTS.benefit3Desc },
    'futureresidentsclub.benefit4_title':  { label: 'Commitment Free Title',       type: 'richtext', defaultValue: DEFAULTS.benefit4Title },
    'futureresidentsclub.benefit4_desc':   { label: 'Commitment Free Description', type: 'richtext', defaultValue: DEFAULTS.benefit4Desc },
    'futureresidentsclub.benefit5_title':  { label: 'Get to Know Us Title',        type: 'richtext', defaultValue: DEFAULTS.benefit5Title },
    'futureresidentsclub.benefit5_desc':   { label: 'Get to Know Us Description',  type: 'richtext', defaultValue: DEFAULTS.benefit5Desc },
    'futureresidentsclub.benefit6_title':  { label: 'Joining Is Easy Title',       type: 'richtext', defaultValue: DEFAULTS.benefit6Title },
    'futureresidentsclub.benefit6_desc':   { label: 'Joining Is Easy Description', type: 'richtext', defaultValue: DEFAULTS.benefit6Desc },
  },
  slotGroups: {
    'futureresidentsclub.how_image': howItWorksImageDef,
    'futureresidentsclub.cta_link': ctaLinkDef,
  },
};

(FutureResidentsClub as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FutureResidentsClub;
